"use client";

import React, { useState } from "react";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { Logos } from "@/data";
import { useScrollReveal } from "@/components/useScrollReveal";
import SubscriptionSimulator from "./SubscriptionSimulator";
import { Calculator, Layers, LayoutGrid } from "lucide-react";

export default function Benefit() {
  const { t } = useTranslation();
  const revealRef = useScrollReveal();
  const [isSimulatorOpen, setIsSimulatorOpen] = useState(false);

  return (
    <section id="benefit" className="py-24 bg-white overflow-hidden">
      <div ref={revealRef} className="w-[92%] max-w-[1800px] mx-auto px-5 lg:px-8">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start mb-14">
          <h2 className="text-h2 md:text-[2.2rem] text-slate-900 leading-tight tracking-tight mb-6">
            {t("benefit.title")}
          </h2>

          <div>
            <p className="text-base md:text-lg leading-relaxed text-slate-600 mb-8">
              {t("benefit.desc")}
            </p>

            {/* Buttons Container */}
            <div className="flex flex-wrap items-center gap-3">
              <a
                href="https://drive.google.com/drive/folders/1IUJrQVosqrnn7kQK9oGR9T0MZciOfXiW"
                target="_blank" rel="noopener noreferrer"
                className="group inline-flex items-center gap-4 bg-gradient-to-r from-[#0696a8] to-[#0891b2] text-white pr-6 pl-2 py-2 rounded-full font-bold shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <span className="flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full p-2.5">
                  <svg xmlns="http://www.w3.org/2000/svg" height="20px" width="20px" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 2c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6H6zm7 7V3.5L18.5 9H13z" />
                  </svg>
                </span>
                <span className="text-sm">{t("benefit.btn_brosur")}</span>
              </a>

              <button
                onClick={() => setIsSimulatorOpen(true)}
                className="group inline-flex items-center gap-3 bg-white text-[#0891b2] pr-5 pl-2 py-2 rounded-full font-bold shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border-2 border-[#0891b2]/20"
              >
                <span className="flex items-center justify-center bg-[#0891b2]/10 rounded-full p-2.5">
                  <Calculator size={18} />
                </span>
                <span className="text-sm">{t("benefit.btn_simulator")}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Bento grid: col1 full/solid tall, col2 35:65, col3 65:35 (reversed) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Column 1 - full height, solid: Penilaian Guru & Siswa */}
          <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-b from-[#1bacee] to-[#08547e] p-8 pb-0 flex flex-col min-h-[420px] lg:h-[700px]">
            <div aria-hidden className="pointer-events-none absolute inset-0 opacity-55 [background-image:radial-gradient(ellipse_130%_42%_at_50%_108%,transparent_48%,rgba(7,147,174,0.72)_49%,rgba(7,147,174,0.72)_66%,transparent_67%),radial-gradient(ellipse_130%_42%_at_50%_82%,transparent_48%,rgba(255,255,255,0.18)_49%,rgba(255,255,255,0.18)_66%,transparent_67%)]" />
            <h3 className="text-xl md:text-2xl font-bold text-white leading-tight">{t("benefit.items.assessment.title")}</h3>
            <p className="mt-3 text-sm md:text-base leading-relaxed text-white/90">{t("benefit.items.assessment.desc")}</p>
            <div className="relative mt-6 flex-1 flex items-end justify-center">
              <Image
                src={Logos.benefitAssessment}
                alt={t("benefit.items.assessment.title")}
                width={280} height={560}
                className="w-full max-w-none h-auto translate-y-10 object-contain object-bottom drop-shadow-2xl"
              />
            </div>
          </div>

          {/* Column 2 - split 35:65 (top smaller, bottom larger) */}
          <div className="grid grid-rows-1 lg:grid-rows-[38fr_62fr] gap-6 lg:h-[700px]">
            {/* Sistem Pendaftaran Siswa - icon only */}
            <div className="relative rounded-[2rem] bg-[#0793ae] p-8 flex flex-col justify-center gap-4 min-h-[220px]">
              <div aria-hidden className="pointer-events-none absolute inset-0 rounded-[2rem] opacity-75 [background-image:radial-gradient(ellipse_at_100%_20%,rgba(255,255,255,0.42)_0,transparent_42%),radial-gradient(ellipse_at_88%_100%,rgba(3,84,126,0.5)_0,transparent_48%),linear-gradient(110deg,transparent_0%,transparent_38%,rgba(255,255,255,0.08)_64%,rgba(3,84,126,0.32)_100%)]" />
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20">
                <Layers className="text-white" size={26} />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-white leading-tight">{t("benefit.items.enrollment.title")}</h3>
              <p className="text-sm md:text-base leading-relaxed text-white/90">{t("benefit.items.enrollment.desc")}</p>
            </div>

            {/* Sistem Manajemen Pembelajaran - browser mockup */}
            <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#29B6F6] to-[#0EA5E9] p-8 pb-0 flex flex-col min-h-[300px]">
              <div aria-hidden className="pointer-events-none absolute inset-0 opacity-90 [background-image:radial-gradient(ellipse_75%_60%_at_100%_0%,rgba(3,84,126,0.9)_0,transparent_58%),radial-gradient(ellipse_70%_65%_at_0%_100%,rgba(255,255,255,0.48)_0,transparent_54%),linear-gradient(145deg,transparent_0%,transparent_32%,rgba(255,255,255,0.2)_55%,rgba(3,84,126,0.65)_82%,rgba(255,255,255,0.3)_100%)]" />
              <h3 className="text-xl md:text-2xl font-bold text-white leading-tight">{t("benefit.items.lms.title")}</h3>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-white/90 max-w-[85%]">{t("benefit.items.lms.desc")}</p>
              <div className="relative mt-12 flex-1 min-h-[220px]">
                <Image
                  src={Logos.benefitLms}
                  alt={t("benefit.items.lms.title")}
                  fill
                  sizes="(max-width: 1024px) 100vw, 33vw"
                  style={{ objectPosition: "center" }}
                  className="object-contain"
                />
              </div>
            </div>
          </div>

          {/* Column 3 - split 65:35 (top larger, bottom smaller) reversed */}
          <div className="grid grid-rows-1 lg:grid-rows-[65fr_35fr] gap-6 lg:h-[700px]">
            {/* Modul Keuangan - illustration */}
            <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#29B6F6] to-[#0EA5E9] p-8 flex flex-col min-h-[300px]">
              <div aria-hidden className="pointer-events-none absolute inset-0 opacity-50 [background-image:repeating-linear-gradient(115deg,transparent_0,transparent_54px,rgba(255,255,255,0.22)_55px,transparent_57px,transparent_98px,rgba(7,147,174,0.55)_99px,transparent_101px),radial-gradient(circle_at_82%_18%,rgba(255,255,255,0.35)_0,transparent_40%),radial-gradient(circle_at_15%_78%,rgba(7,147,174,0.5)_0,transparent_38%)]" />
              <h3 className="text-xl md:text-2xl font-bold text-white leading-tight">{t("benefit.items.finance.title")}</h3>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-white/90 max-w-[65%]">{t("benefit.items.finance.desc")}</p>
              <div className="relative -mr-8 mt-6 -mb-4 flex-1 min-h-[140px]">
                <Image
                  src={Logos.benefitFinance}
                  alt={t("benefit.items.finance.title")}
                  fill
                  sizes="(max-width: 1024px) 100vw, 33vw"
                  style={{ objectPosition: "bottom right" }}
                  className="object-contain"
                />
              </div>
            </div>

            {/* Sistem Administrasi - dark icon card */}
            <div className="relative rounded-[2rem] bg-gradient-to-br from-[#075985] to-[#0B4A6F] p-8 flex flex-col justify-center gap-4 min-h-[220px]">
              <div aria-hidden className="pointer-events-none absolute inset-0 rounded-[2rem] opacity-40 [background-image:radial-gradient(circle_at_80%_20%,rgba(7,147,174,0.72)_0,transparent_34%),radial-gradient(circle_at_15%_85%,rgba(255,255,255,0.18)_0,transparent_28%)]" />
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10">
                <LayoutGrid className="text-white" size={26} />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-white leading-tight">{t("benefit.items.administration.title")}</h3>
              <p className="text-sm md:text-base leading-relaxed text-white/90">{t("benefit.items.administration.desc")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Simulator Modal */}
      <SubscriptionSimulator
        isOpen={isSimulatorOpen}
        onClose={() => setIsSimulatorOpen(false)}
      />
    </section>
  );
}
